import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoginForm, RegisterForm } from '@/components/auth';
import { useAuth } from '@/hooks';
import type { AuthMode } from '@/components/layout/AuthLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { isEmailVerificationError } from '@/components/layout/AuthContext';
import { VerificationPending } from '@/components/auth/VerificationPending';
import { useUiStore } from '@/store/uiStore';
import { getUtmEchoConfigBySlug } from '@/components/layout/auth/utmEchoConfig';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  agreed: boolean;
}

/**
 * LandingEchoPage — UTM/campaign landing page.
 * Mounted at `/:echoSlug` — resolves the slug against utmEchoConfig.
 * Returns null (renders nothing) when the slug is not a known UTM echo,
 * letting React Router fall through to the catch-all 404.
 */
export function LandingEchoPage() {
  const { echoSlug = '' } = useParams<{ echoSlug: string }>();
  const config = getUtmEchoConfigBySlug(echoSlug);

  const { isAuthenticated, isLoading, error, login, register } = useAuth();
  const openModal = useUiStore((state) => state.openModal);
  const [view, setView] = React.useState<AuthMode>('login');
  const [verificationEmail, setVerificationEmail] = React.useState<string | null>(null);
  const navigate = useNavigate();

  // Persist the UTM echo slug so WelcomeModal can redirect to this echo's chat
  React.useEffect(() => {
    if (config) {
      sessionStorage.setItem('utm_echo_slug', config.slug);
    }
  }, [config]);

  const handleChatNow = () => {
    if (config) {
      navigate(`/messages/${config.slug}`);
    }
  };

  const handleLogin = async (data: { email: string; password: string }) => {
    try {
      await login(data.email, data.password);
    } catch (err) {
      if (isEmailVerificationError(err)) {
        setVerificationEmail(err.email);
        setView('verify-pending');
      }
    }
  };

  const handleRegister = async (data: RegisterFormData) => {
    try {
      const result = await register({
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        accepted_terms: data.agreed,
        role: 'fan',
      });

      if (result && 'requires_email_verification' in result) {
        setVerificationEmail(result.email);
        setView('verify-pending');
      }
    } catch (err) {
      if (isEmailVerificationError(err)) {
        setVerificationEmail(err.email);
        setView('verify-pending');
      }
    }
  };

  const handleSwitch = () => {
    if (view === 'forgot' || view === 'verify-pending') {
      setVerificationEmail(null);
      setView('login');
    } else {
      setView(view === 'login' ? 'register' : 'login');
    }
  };

  const formId = 'auth-form';
  const submitLabel = view === 'register' ? 'CREATE ACCOUNT' : 'SIGN IN';
  const dividerText = view === 'register' ? 'or register with email' : 'or sign in with email';

  return (
    <AuthLayout
      authType={view}
      onSwitch={handleSwitch}
      isLoading={isLoading}
      submitLabel={submitLabel}
      dividerText={dividerText}
      formId={formId}
      utmEcho={echoSlug}
      isAuthenticated={isAuthenticated}
      onChatNow={handleChatNow}
    >
      {view === 'verify-pending' && verificationEmail ? (
        <VerificationPending email={verificationEmail} onBackToLogin={() => setView('login')} />
      ) : view === 'register' ? (
        <RegisterForm
          formId={formId}
          onSubmit={handleRegister}
          error={error}
          isLoading={isLoading}
          onTermsClick={() => openModal('terms')}
        />
      ) : (
        <LoginForm
          formId={formId}
          onSubmit={handleLogin}
          onForgot={() => setView('forgot')}
          error={error}
          isLoading={isLoading}
        />
      )}
    </AuthLayout>
  );
}
